import React from 'react';

import { classnames } from '@/utils/classnames.ts';

export interface BadgeProps extends Omit<React.ComponentPropsWithRef<'span'>, 'children'> {
    // Without one, or at 0, it's a dot.
    count?: number;
    // Filled with the primary color rather than muted, e.g. when something's new besides what's counted.
    isEmphasized?: boolean;
    // `negative` for something overdue or failed, which wins over the emphasis.
    tone?: 'neutral' | 'negative';
    // `lg` next to a section's title.
    size?: 'sm' | 'lg';
    // What it marks, said in words for screen readers, e.g. "Neu"; without one, the label of what it marks says it.
    label?: string;
}

const MAX_COUNT = 9;

// A count or a dot next to what it's about, e.g. how much waits in a section and whether something's new there. It's
// only seen; its label, or the label of what it marks, says the same in words.
const Badge = ({ label, ...props }: BadgeProps) =>
    label ? (
        <>
            <BadgeMark {...props} />
            <span className="sr-only">{label}</span>
        </>
    ) : (
        <BadgeMark {...props} />
    );

const BadgeMark = ({
    count = 0,
    isEmphasized = false,
    tone = 'neutral',
    size = 'sm',
    className,
    ...props
}: Omit<BadgeProps, 'label'>) => {
    const toneClassName = classnames(
        tone === 'negative' && 'bg-negative text-white',
        tone === 'neutral' && (isEmphasized || count === 0) && 'bg-primary text-on-primary',
        tone === 'neutral' && !isEmphasized && count > 0 && 'bg-surface-hover text-text-muted'
    );

    if (count === 0) {
        return (
            <span
                aria-hidden
                {...props}
                className={classnames(
                    'shrink-0 rounded-full select-none',
                    size === 'sm' ? 'size-2' : 'size-2.5',
                    toneClassName,
                    className
                )}
            />
        );
    }
    return (
        <span
            aria-hidden
            {...props}
            className={classnames(
                'flex shrink-0 items-center justify-center rounded-full leading-none font-semibold tabular-nums select-none',
                size === 'sm' ? 'h-4.5 min-w-4.5 px-1.5 text-2xs' : 'h-6 min-w-6 px-2 text-sm',
                toneClassName,
                className
            )}
        >
            {count > MAX_COUNT ? `${MAX_COUNT}+` : count}
        </span>
    );
};

export default Badge;
